import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { fetchFlags, setFlagStage, setFlagKill } from '../../lib/api/flags';
import { Button, PageHeader, StatusBadge, EmptyState } from '../../components/ui';
import type { StatusTone } from '../../components/ui';
import type { FlagStage, FlagView } from '../../../../shared/types/flags';
import { STAGES } from '../../../../shared/types/flags';

/**
 * /admin/flags — global feature-flag control (trunk-based CI/CD + feature flags).
 *
 * GA by default: most features have no flag. This page lists the flags that DO
 * exist and lets a platform admin set each flag's GLOBAL stage (off → beta cohort
 * → everyone) and kill/revive it (emergency global off). Changes take effect on
 * the next agent launch — no redeploy. Per-project overrides + beta-cohort
 * membership are managed elsewhere (the /flag command + project pages).
 */
export const FlagsAdminPage: React.FC = () => {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['flags'], queryFn: fetchFlags });

  const stageMutation = useMutation({
    mutationFn: ({ name, stage }: { name: string; stage: FlagStage }) => setFlagStage(name, stage),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flags'] }),
  });
  const killMutation = useMutation({
    mutationFn: ({ name, killed }: { name: string; killed: boolean }) => setFlagKill(name, killed),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flags'] }),
  });

  const flags = query.data?.flags ?? [];
  const busy = stageMutation.isPending || killMutation.isPending;
  const errorMsg =
    (stageMutation.error as Error | undefined)?.message ??
    (killMutation.error as Error | undefined)?.message ??
    null;

  return (
    <div data-testid="flags-admin-page" className="min-h-screen bg-slate-50">
      <Header title="Flags" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <PageHeader
          title={<>Feature flags <span className="text-slate-400 font-normal">· global</span></>}
          description="GA by default — most features ship without a flag. These flags exist to stage a rollout (rollout) or to kill-switch a live GA feature (ops). Stage + kill changes are global and take effect on the next agent launch, no redeploy."
        />

        {errorMsg && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3">
            {errorMsg}
          </div>
        )}

        {query.isLoading ? (
          <div className="text-sm text-slate-500">Loading flags…</div>
        ) : query.isError ? (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3">
            {(query.error as Error).message}
          </div>
        ) : flags.length === 0 ? (
          <EmptyState
            title="No feature flags declared"
            description="Every feature is GA. Add a flag in agent/lib/flags.js only to stage a rollout or to gain a kill-switch."
          />
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 font-medium">Flag</th>
                  <th className="px-4 py-3 font-medium">Kind</th>
                  <th className="px-4 py-3 font-medium">Global stage</th>
                  <th className="px-4 py-3 font-medium">State</th>
                  <th className="px-4 py-3 font-medium text-right">Kill-switch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {flags.map((f) => (
                  <FlagRow
                    key={f.name}
                    flag={f}
                    busy={busy}
                    onStage={(stage) => stageMutation.mutate({ name: f.name, stage })}
                    onKill={(killed) => killMutation.mutate({ name: f.name, killed })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {query.data?.updated_at && (
          <p className="text-xs text-slate-400">
            Global flag state last changed {new Date(query.data.updated_at).toLocaleString()}
            {query.data.updated_by ? ` by ${query.data.updated_by}` : ''}.
          </p>
        )}
      </main>
    </div>
  );
};

// ---------------------------------------------------------------------------

const KIND_TONE: Record<string, string> = {
  rollout: 'bg-blue-50 text-blue-700 border-blue-200',
  ops: 'bg-violet-50 text-violet-700 border-violet-200',
};

const STAGE_TONE: Record<FlagStage, StatusTone> = {
  off: 'neutral',
  beta: 'warning',
  ga: 'success',
};

const FlagRow: React.FC<{
  flag: FlagView;
  busy: boolean;
  onStage: (stage: FlagStage) => void;
  onKill: (killed: boolean) => void;
}> = ({ flag, busy, onStage, onKill }) => {
  return (
    <tr className={flag.killed ? 'bg-rose-50/40' : undefined}>
      <td className="px-4 py-3 align-top">
        <div className="font-mono text-slate-900">{flag.name}</div>
        <div className="text-xs text-slate-500 mt-0.5 max-w-md">{flag.description}</div>
        <div className="text-[11px] text-slate-400 mt-1">
          owner {flag.owner} · added {flag.created}
          {flag.retire_by ? ` · retire by ${flag.retire_by}` : ''}
        </div>
        {flag.staleAtGa && (
          <div className="text-[11px] text-amber-700 mt-1">
            ⚠ rollout flag at GA — delete it and make the code unconditional.
          </div>
        )}
        {flag.pastRetireBy && (
          <div className="text-[11px] text-rose-700 mt-1">⏰ past retire-by date — overdue for deletion.</div>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${KIND_TONE[flag.kind] ?? ''}`}>
          {flag.kind}
        </span>
      </td>
      <td className="px-4 py-3 align-top">
        <select
          value={flag.stage}
          disabled={busy || flag.killed}
          onChange={(e) => onStage(e.target.value as FlagStage)}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm disabled:opacity-50"
          aria-label={`Global stage for ${flag.name}`}
        >
          {STAGES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {!flag.stageIsOverride && (
          <div className="text-[11px] text-slate-400 mt-1">registry default</div>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        {flag.killed ? (
          <StatusBadge tone="danger">KILLED</StatusBadge>
        ) : (
          <StatusBadge tone={STAGE_TONE[flag.stage]}>
            {flag.stage === 'ga' ? 'on · everyone' : flag.stage === 'beta' ? 'beta cohort' : 'off'}
          </StatusBadge>
        )}
      </td>
      <td className="px-4 py-3 align-top text-right">
        {flag.killed ? (
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => onKill(false)}>
            Revive
          </Button>
        ) : (
          <Button variant="danger" size="sm" disabled={busy} onClick={() => onKill(true)}>
            Kill
          </Button>
        )}
      </td>
    </tr>
  );
};
